import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Download, Loader2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, SelectF, CheckF, inputCls } from "../admin/AFields";
import { Modal } from "../admin/Participants";

const BLANK = { name: "", mobile: "", whatsapp: "", email: "", organisation: "", group: "Other", groups: [], notes: "", language: "english", city: "", consent: true, opt_out: false, active: true };
const CSV_SAMPLE = "Name,Mobile,WhatsApp,Email,Organization,Groups,Language,City,Notes\nAmit Kumar,9876543210,9876543210,amit@example.com,Bar Association,Advocates;Clients,english,Prayagraj,Met at event";

export default function StudioContacts() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("");
  const [editing, setEditing] = useState(null);
  const [newGroup, setNewGroup] = useState("");
  const [importing, setImporting] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [xlsxB64, setXlsxB64] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkGroup, setBulkGroup] = useState("");
  const fileRef = useRef(null);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (group) params.set("group", group);
    api.get(`/greetings/contacts?${params}`).then((r) => { setItems(r.data); setSelected([]); }).catch((e) => toast.error(errMsg(e)));
    api.get("/greetings/groups").then((r) => setGroups(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [group]); // eslint-disable-line

  const save = async () => {
    try {
      if (newGroup.trim() && !groups.some((g) => g.name.toLowerCase() === newGroup.trim().toLowerCase())) {
        await api.post("/greetings/groups/save", { name: newGroup.trim() });
      }
      const payload = { ...editing, groups: newGroup.trim() ? [...(editing.groups || []), newGroup.trim()] : editing.groups };
      await api.post("/greetings/contacts/save", payload);
      toast.success("Contact saved"); setEditing(null); setNewGroup(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.delete(`/greetings/contacts/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(null);
    if (f.name.toLowerCase().endsWith(".xlsx")) {
      const reader = new FileReader();
      reader.onload = () => { setXlsxB64(String(reader.result)); setCsvText(""); toast.success(`Loaded ${f.name} — validate before importing`); };
      reader.readAsDataURL(f);
    } else {
      const reader = new FileReader();
      reader.onload = () => { setCsvText(String(reader.result || "")); setXlsxB64(""); toast.success(`Loaded ${f.name} — validate before importing`); };
      reader.readAsText(f);
    }
  };

  const runImport = async (confirm) => {
    if (!csvText.trim() && !xlsxB64) { toast.error("Choose a CSV or XLSX file first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/greetings/contacts/import", { csv: csvText, xlsx_base64: xlsxB64, confirm });
      if (!confirm) setPreview(r.data);
      else {
        toast.success(`Imported ${r.data.imported} contacts (${r.data.duplicates} duplicates, ${r.data.invalid} invalid skipped)`);
        setImporting(false); setCsvText(""); setXlsxB64(""); setPreview(null); load();
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/studio/contacts/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "contacts.csv";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Contacts exported");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = items.length > 0 && selected.length === items.length;

  const bulk = async (action) => {
    if (!selected.length) { toast.error("Select contacts first"); return; }
    if (action === "delete" && !window.confirm(`Delete ${selected.length} selected contacts?`)) return;
    try {
      await api.post("/greetings/contacts/bulk", { ids: selected, action, group: bulkGroup });
      toast.success(action === "delete" ? `${selected.length} contacts deleted` : `Added to group "${bulkGroup}"`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const contactGroups = (c) => (c.groups && c.groups.length ? c.groups : [c.group || "Other"]);

  return (
    <div data-testid="studio-contacts">
      <SEO title="Contacts" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Contacts & Groups</h1>
          <p className="mt-1 text-sm text-slate-500">Greeting audience with consent tracking, multi-group membership and duplicate detection by phone number.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="rounded-md border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-navy" data-testid="contacts-export-btn"><Download size={15} className="mr-1 inline" /> Export</button>
          <button onClick={() => { setImporting(true); setPreview(null); setCsvText(""); setXlsxB64(""); }} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="contacts-import-btn"><Upload size={15} /> Import CSV / XLSX</button>
          <button onClick={() => setEditing({ ...BLANK })} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="contact-new-btn"><Plus size={15} /> Add Contact</button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / mobile / email…" className={`${inputCls} !w-64`} data-testid="contacts-search" />
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-navy" data-testid="contacts-search-btn">Search</button>
        <select value={group} onChange={(e) => setGroup(e.target.value)} className={`${inputCls} !w-44`} data-testid="contacts-group-filter">
          <option value="">All groups</option>
          {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
        </select>
      </div>

      {selected.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-saffron-500/40 bg-saffron-100/60 px-4 py-3" data-testid="bulk-bar">
          <span className="text-xs font-bold text-navy">{selected.length} selected</span>
          <input list="bulk-groups" value={bulkGroup} onChange={(e) => setBulkGroup(e.target.value)} placeholder="Group name…" className={`${inputCls} !w-44 !py-1.5`} data-testid="bulk-group-name" />
          <datalist id="bulk-groups">{groups.map((g) => <option key={g.id} value={g.name} />)}</datalist>
          <button onClick={() => bulk("add_group")} disabled={!bulkGroup.trim()} className="rounded-md bg-navy px-3.5 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="bulk-add-group">Add to Group</button>
          <button onClick={() => bulk("delete")} className="rounded-md bg-red-50 px-3.5 py-1.5 text-xs font-bold text-red-500" data-testid="bulk-delete">Delete Selected</button>
          <button onClick={() => setSelected([])} className="text-xs font-semibold text-slate-500">Clear</button>
        </div>
      )}

      <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3"><input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? [] : items.map((c) => c.id))} className="h-4 w-4 accent-saffron-500" aria-label="Select all" data-testid="contacts-select-all" /></th>
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Mobile</th><th className="px-4 py-3">WhatsApp</th>
              <th className="px-4 py-3">Groups</th><th className="px-4 py-3">Consent</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-gray-50" data-testid={`contact-row-${c.id}`}>
                <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} className="h-4 w-4 accent-saffron-500" aria-label={`Select ${c.name}`} data-testid={`contact-select-${c.id}`} /></td>
                <td className="px-4 py-3 font-semibold text-navy">{c.name}<span className="block text-xs font-normal text-slate-400">{[c.organisation, c.city].filter(Boolean).join(" · ")}</span></td>
                <td className="px-4 py-3">{c.mobile}</td>
                <td className="px-4 py-3">{c.whatsapp || "—"}</td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{contactGroups(c).map((g) => <span key={g} className="rounded-full bg-navy-50 px-2 py-0.5 text-[11px] font-bold text-navy">{g}</span>)}</div></td>
                <td className="px-4 py-3">
                  {c.opt_out ? <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-bold text-red-500">OPTED OUT</span>
                    : c.consent !== false ? <span className="rounded-full bg-brandgreen-100 px-2.5 py-0.5 text-xs font-bold text-brandgreen">CONSENTED</span>
                    : <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-500">NO CONSENT</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => setEditing({ ...BLANK, ...c, groups: contactGroups(c) })} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`contact-edit-${c.id}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(c)} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`contact-del-${c.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">No contacts found. Add manually or import a CSV/XLSX.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? `Edit — ${editing.name}` : "New Contact"} testid="contact-editor" wide>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Full Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
            <TextF label="Mobile" value={editing.mobile} onChange={(v) => setEditing({ ...editing, mobile: v })} />
            <TextF label="WhatsApp Number" value={editing.whatsapp} onChange={(v) => setEditing({ ...editing, whatsapp: v })} />
            <TextF label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />
            <TextF label="Organization" value={editing.organisation} onChange={(v) => setEditing({ ...editing, organisation: v })} />
            <TextF label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} />
          </div>
          <div className="mt-5">
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Groups (a contact may belong to many)</label>
            <div className="flex flex-wrap gap-2" data-testid="contact-groups-picker">
              {groups.map((g) => (
                <button key={g.id} type="button"
                  onClick={() => setEditing({ ...editing, groups: (editing.groups || []).includes(g.name) ? editing.groups.filter((x) => x !== g.name) : [...(editing.groups || []), g.name] })}
                  data-testid={`contact-group-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-bold ${(editing.groups || []).includes(g.name) ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>{g.name}</button>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="New group name…" className={`${inputCls} !w-56`} data-testid="contact-new-group" />
            </div>
          </div>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <SelectF label="Preferred Language" value={editing.language} onChange={(v) => setEditing({ ...editing, language: v })} options={["english", "hindi", "bilingual"]} />
          </div>
          <div className="mt-5">
            <AreaF label="Notes" value={editing.notes} onChange={(v) => setEditing({ ...editing, notes: v })} rows={2} />
          </div>
          <div className="mt-5 flex flex-wrap gap-6">
            <CheckF label="WhatsApp consent given" value={editing.consent} onChange={(v) => setEditing({ ...editing, consent: v })} />
            <CheckF label="Opted out" value={editing.opt_out} onChange={(v) => setEditing({ ...editing, opt_out: v })} />
            <CheckF label="Active" value={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="contact-save">Save</button>
          </div>
        </Modal>
      )}

      {importing && (
        <Modal onClose={() => setImporting(false)} title="Import Contacts (CSV or XLSX)" testid="contact-import-modal" wide>
          <p className="text-xs text-slate-500">Headers: <code className="rounded bg-navy-50 px-1.5 py-0.5">Name, Mobile, WhatsApp, Email, Organization, Groups, Language, City, Notes</code> — Groups may contain multiple groups separated by <code className="rounded bg-navy-50 px-1">;</code>. Duplicates are detected by phone number.</p>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={() => fileRef.current?.click()} className="btn-dark !rounded-md !px-4 !py-2 text-xs" data-testid="csv-pick-btn"><Upload size={13} /> Choose File</button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={pickFile} data-testid="csv-file-input" />
            {(csvText || xlsxB64) && <span className="text-xs font-bold text-brandgreen">{xlsxB64 ? "XLSX" : `${csvText.split("\n").filter(Boolean).length - 1} rows`} loaded</span>}
          </div>
          <details className="mt-3">
            <summary className="cursor-pointer text-xs font-bold text-saffron-600">View sample format</summary>
            <pre className="mt-2 overflow-x-auto rounded-md bg-navy-50 p-3 text-xs text-slate-600">{CSV_SAMPLE}</pre>
          </details>
          {preview && (
            <div className="mt-4 space-y-3" data-testid="import-preview">
              <div className="flex gap-3 text-xs font-bold">
                <span className="rounded-full bg-brandgreen-100 px-3 py-1 text-brandgreen">{preview.valid_count} valid</span>
                <span className="rounded-full bg-saffron-100 px-3 py-1 text-saffron-600">{preview.duplicates.length} duplicates (skipped)</span>
                <span className="rounded-full bg-red-50 px-3 py-1 text-red-500">{preview.invalid.length} invalid (skipped)</span>
              </div>
              {preview.invalid.length > 0 && (
                <div className="max-h-32 overflow-y-auto rounded-md border border-red-100 bg-red-50/50 p-3 text-xs">
                  {preview.invalid.map((r, i) => <p key={i} className="text-red-600">Row {r.row}: {r.name || "—"} ({r.mobile || "no number"}) — {r.reason}</p>)}
                </div>
              )}
              {preview.duplicates.length > 0 && (
                <div className="max-h-32 overflow-y-auto rounded-md border border-saffron-500/30 bg-saffron-100/50 p-3 text-xs">
                  {preview.duplicates.map((r, i) => <p key={i} className="text-saffron-600">Row {r.row}: {r.name} ({r.mobile}) — already exists</p>)}
                </div>
              )}
            </div>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setImporting(false)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={() => runImport(false)} disabled={busy || (!csvText && !xlsxB64)} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="csv-validate-btn">
              {busy && !preview && <Loader2 size={14} className="animate-spin" />} Validate
            </button>
            <button onClick={() => runImport(true)} disabled={busy || !preview || preview.valid_count === 0} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="csv-confirm-btn">
              {busy && preview && <Loader2 size={14} className="animate-spin" />} Import {preview ? preview.valid_count : 0} Contacts
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
